<template>
	<div class="xs-12">
		<!-- Headline -->
		<div slot="headline" class="fs5 lh125 fw7 c1 mb2">{{ $t("actions") }}</div>

		<!-- Body -->
		<j-card class="bg5 bg4--l3">
			<!-- Request Payment -->
			<div
				v-if="$store.state.settings.policies.actions.hasRequestPayment"
				class="flex xs-12 xs-middle xs-between p2 bb1 bc7"
			>
				<div class="fs3 fw7 c6 lh125">{{ $t("request_payment") }}</div>
				<j-button
					size="xsmall"
					:isLoading="isRequestPaymentLoading"
					v-tooltip="requestPaymentTooltip"
					@click="requestPayment"
					action="requestPayment"
					>{{ $t("request") }}</j-button
				>
			</div>

			<!-- Send Documents to Client -->
			<div
				v-if="$store.state.settings.policies.actions.hasSendConfirmation"
				class="flex xs-12 xs-middle xs-between p2"
			>
				<div class="fs3 fw7 c6 lh125">
					{{ $t("send_issuance_confirmation") }}
				</div>
				<j-button
					size="xsmall"
					:isLoading="isSendConfirmationLoading"
					v-tooltip="sendConfirmationTooltip"
					@click="sendConfirmation"
					action="sendConfirmation"
					>{{ $t("send") }}</j-button
				>
			</div>
		</j-card>
	</div>
</template>

<script>
import JCard from "@/components/JCard"
import JButton from "@/components/JButton"
import get from "lodash.get"
import api from "../api/"

// Templates
// TODO: this will be refactored one beautiful day
// we should have better mechanics for those templates than hardcoding in codebase
import { counteroffer } from "@/clientSettings/templates/asia_uat.js"
import { payment as getPaymentNotification } from "@/clientSettings/templates/asia_uat.js"
/**
 * This component is responsible for numerous actions that can happen on policy
 * It's a smart component that is calling APIs
 */
export default {
	components: { JCard, JButton },
	props: {
		policyId: {
			type: String,
			required: true,
			default: ""
		},
		policyNumber: {
			type: String,
			required: true,
			default: ""
		},
		amount: {
			type: Number,
			required: true,
			default: 0
		},
		currencyCode: {
			type: String,
			required: true,
			default: ""
		},
		/**
		 * TODO: hacking here
		 * We are passing the whole data object we get for quote
		 * It's a policy type thta we get from GraphQL
		 * We do it because we need to construct email template to be sent and for that we need some data to populate the template
		 * It shouldn't work like this, but we have improvised email templating now
		 * After we have better system for email templating handling, this should be removed
		 */
		context: {
			type: Object,
			required: true,
			default: () => {}
		}
	},
	data() {
		return {
			isRequestPaymentLoading: false,
			isSendConfirmationLoading: false
		}
	},
	computed: {
		requestPaymentTooltip() {
			return {
				placement: "auto",
				content: this.$t("request_payment_description"),
				delay: { show: 500 }
			}
		},
		sendConfirmationTooltip() {
			return {
				placement: "auto",
				content: this.$t("send_documents_description"),
				delay: { show: 500 }
			}
		}
	},
	methods: {
		async sendConfirmation() {
			this.isSendConfirmationLoading = true
			// 1: Get all data for policy so that we can populate templates
			const policy = await api.policy({
				where: { id: this.policyId }
			})
			if (policy.errors) {
				this.$store.dispatch("showError", res.errors)
				this.isSendConfirmationLoading = true
				return
			}
			const payload = policy.data.policies.list[0]
			const toEmail = payload.contractHolder.contacts
				.filter(item => item.type === "email")
				.map(item => item.value)
				.toString()
			// TODO: populate with proper data
			// const {
			// 	premium,
			// 	exclusions,
			// 	product
			// } = this.context.acceptedOffer
			const res = await api.sendNotification({
				input: {
					emailMessage: counteroffer({
						toEmail: toEmail,
						payload,
						type: "policy",
						context: {
							pricing: this.context.acceptedOffer.premium,
							exclusions: this.context.acceptedOffer.exclusions,
							product: this.context.acceptedOffer.product
						}
					})
				}
			})
			this.isSendConfirmationLoading = false
			// Errros
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}
			// Success
			this.$store.dispatch("showSuccess", this.$t("email_was_sent"))
		},
		async requestPayment() {
			// NOTE: This should be refactored to take into account payment providers
			// The flow should be similar to what we do in Quote - QuoteActions
			// Start of loading
			this.isRequestPaymentLoading = true
			const redirectToUrl = `https://api.asia.covergo.com/j/${
				this.$store.state.tenantId
			}/covergo_crm/(transactionId)/p/0/en`
			// 1: Initialize payment call
			const res = await api.initializeTransaction({
				input: {
					policyId: this.policyId,
					amount: this.amount,
					currencyCode: this.currencyCode,
					redirectToUrl
				}
			})
			// Handle errors
			// This query is rather special so we have to check for these errors
			if (res.errors || res.data.initializeTransaction.errors) {
				this.$store.dispatch("showError", this.$t("errors.general"))
				this.isRequestPaymentLoading = false // end loading if error
				return
			}
			// When successfull, send email notification with a link to our Jetco bridge app
			const url = get(res, "data.initializeTransaction.value.url")
			const token = get(res, "data.initializeTransaction.value.token")
			// 2: Send email
			/**
			 * Helper function to get email from contacts of holder
			 */
			const getHolderEmail = contacts => {
				const found = contacts.find(item => item.type === "email")
				return found.value
			}
			// Send
			const sendEmailRes = await api.sendNotification({
				input: {
					emailMessage: getPaymentNotification({
						toEmail: getHolderEmail(this.context.contractHolder.contacts),
						policyNumber: this.policyNumber,
						policyHolders: [
							this.context.contractHolder.name,
							this.context.otherContractHolders
								? this.context.otherContractHolders.map(item => item.name)
								: ""
						],
						insuredName: get(this, "context.contractInsured[0].name", ""),
						paymentLink: `https://payments.asia.covergo.com/bridge?url=${encodeURIComponent(
							url
						)}&token=${encodeURIComponent(token)}&provider=jetco`
					})
				}
			})
			// Check errors
			if (sendEmailRes.errors) {
				this.$store.dispatch("showError", sendEmailRes.errors)
				this.isRequestPaymentLoading = false
				return
			}
			// Show success message
			this.$store.dispatch("showSuccess", this.$t("email_was_sent_to_client"))
			// End of loading
			this.isRequestPaymentLoading = false
		}
	}
}
</script>
